import { useStore } from '@nanostores/react';
import type { TextSearchMatch } from '@webcontainer/api';
import { useRef, useCallback, useState, memo, useImperativeHandle, forwardRef, useEffect } from 'react';
import { LegacyIconButton } from '~/components/ui/LegacyIconButton';
import { useElementWidth } from '~/lib/hooks/useElementWidth';
import { useSearchMatch } from '~/lib/hooks/useSearchMatch';
import { useTreeNavigation } from '~/lib/hooks/useTreeNavigation';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { getMonospaceCharCount } from '~/utils/get-monospace-char-count';

const SearchInput = forwardRef<HTMLInputElement>((_props, ref) => {
  const isRegex = useStore(workbenchStore.searchStore.isRegex);
  const caseSensitive = useStore(workbenchStore.searchStore.caseSensitive);

  return (
    <div className="px-3 mt-3 pb-0 relative">
      <label htmlFor="search-input" className="sr-only">
        Search in files
      </label>

      <input
        ref={ref}
        id="search-input"
        type="text"
        placeholder="Search"
        role="searchbox"
        autoCorrect="off"
        autoComplete="off"
        aria-label="Search in files"
        className="bg-bolt-elements-background-depth-4 transition-[background-color] focus:bg-bolt-elements-item-backgroundActive text-sm focus:outline-none placeholder:text-bolt-elements-textTertiary rounded-md h-8.5 pl-2.5 pr-16 py-3 w-full"
        onChange={(event) => workbenchStore.searchStore.setSearchQuery(event.target.value)}
      />

      <div className="flex items-center right-3 pr-1.5 gap-0.5 absolute inset-y-0">
        <LegacyIconButton
          className="bg-transparent"
          onClick={() => workbenchStore.searchStore.toggleCaseSensitivity()}
          title="Match Case"
        >
          <div
            className={classNames(
              'i-lucide:case-sensitive size-4 color-bolt-elements-item-contentDefault scale-125 translate-y-px',
              {
                'color-bolt-elements-item-contentActive': caseSensitive,
              },
            )}
          />
        </LegacyIconButton>
        <LegacyIconButton
          className="bg-transparent"
          onClick={() => workbenchStore.searchStore.toggleRegex()}
          title="Use Regular Expression"
        >
          <div
            className={classNames('i-lucide:regex size-4 color-bolt-elements-item-contentDefault', {
              'color-bolt-elements-item-contentActive': isRegex,
            })}
          />
        </LegacyIconButton>
      </div>
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

type RenderMatchProps = {
  match: TextSearchMatch;
};

const RenderMatch = memo(({ match }: RenderMatchProps) => {
  const { prefix, highlight, suffix, showInitialEllipsis } = useSearchMatch(match, { fontSize: '12px' });

  return (
    <pre className="text-xs truncate">
      {showInitialEllipsis && <span>&hellip;</span>}
      {prefix}
      <mark className="bg-accent-600/30 rounded px-px -mx-px" role="mark" aria-label={`Match: ${highlight}`}>
        <span className="text-bolt-ds-brand">{highlight}</span>
      </mark>
      {suffix}
    </pre>
  );
});

RenderMatch.displayName = 'RenderMatch';

type FileGroupProps = {
  matches: TextSearchMatch[];
  path: string;
  tabIndex: number;
};

const FileGroup = memo(({ matches, path, tabIndex }: FileGroupProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const filename = path.split('/').pop();
  const headingId = `file-heading-${path}`;
  const regionId = `file-region-${path}`;

  return (
    <div className="overflow-hidden" role="listitem">
      <button
        className="group flex items-center gap-1.5 w-full px-3 h-7 bg-transparent text-bolt-elements-item-contentDefault hover:text-bolt-elements-item-contentActive hover:bg-bolt-elements-item-backgroundActive cursor-pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-expanded={!isCollapsed}
        aria-controls={regionId}
        id={headingId}
        tabIndex={tabIndex}
      >
        <div
          className={classNames('i-ph:caret-right size-3 transition-transform shrink-0', {
            'rotate-90': !isCollapsed,
          })}
          aria-hidden="true"
        />
        <div className="text-sm leading-4 truncate">{filename}</div>
        <div
          className="flex items-center justify-center px-1.5 py-0.5 text-xs text-bolt-ds-brand bg-bolt-ds-brandContainer rounded-full ml-auto"
          aria-label={`${matches.length} matches`}
        >
          {matches.length}
        </div>
      </button>

      {!isCollapsed && (
        <div id={regionId} role="region" aria-labelledby={headingId}>
          {matches.map((match, index) => (
            <button
              title={match.preview.text}
              key={`${path}-${match.ranges[0].startColumn}-${match.ranges[0].endColumn}-${match.ranges[0].startLineNumber}-${match.ranges[0].endLineNumber}`}
              className="text-xs overflow-auto pl-7 pr-3 h-7 bg-transparent w-full cursor-pointer hover:bg-bolt-elements-background-depth-3 flex items-center"
              onClick={() =>
                workbenchStore.searchStore.selectSearchResult(`${path}:${match.ranges[0].startLineNumber + 1}`)
              }
              role="link"
              aria-label={`Match in ${filename} at line ${match.ranges[0].startLineNumber + 1}`}
              data-path={path}
              data-match-index={index}
              tabIndex={-1}
            >
              <RenderMatch match={match} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

FileGroup.displayName = 'FileGroup';

const ResultList = memo(() => {
  const results = useStore(workbenchStore.searchStore.resultsArray);
  const showNoResults = useStore(workbenchStore.searchStore.showNoResults);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleTabNavigation = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Tab') {
      const isShiftTab = event.shiftKey;
      const target = event.target as HTMLElement;
      const isHeader = target.matches('button[id^="file-heading-"]');
      const isItem = target.matches('button[role="link"]');

      if (isShiftTab && (isHeader || isItem)) {
        // when using Shift+Tab, skip all results and go to previous focusable element
        event.preventDefault();

        const searchInput =
          containerRef.current?.previousElementSibling?.querySelector<HTMLElement>('input[role="searchbox"]');
        searchInput?.focus();
      }
    }
  }, []);

  const { handleKeyDown } = useTreeNavigation({
    containerRef,
    headerSelector: 'button[id^="file-heading-"]',
    itemSelector: 'button[role="link"]',
    onHeaderExpand: (header) => header.click(),
    onHeaderCollapse: (header) => header.click(),
    onItemSelect: (item) => item.click(),
  });

  const handleKeyboardEvents = useCallback(
    (event: React.KeyboardEvent) => {
      handleTabNavigation(event);
      handleKeyDown(event);
    },
    [handleTabNavigation, handleKeyDown],
  );

  return (
    <div
      ref={containerRef}
      className="text-bolt-elements-item-contentDefault overflow-hidden"
      role="list"
      aria-label="Search results"
      onKeyDown={handleKeyboardEvents}
    >
      <MaxContentLengthTracker />
      {showNoResults && <Message message="No results found" />}
      {results.map(({ path, matches }, index) => (
        <FileGroup key={path} matches={matches} path={path} tabIndex={index === 0 ? 0 : -1} />
      ))}
    </div>
  );
});

ResultList.displayName = 'ResultList';

type MessageProps = {
  message: string;
};

const Message = memo(({ message }: MessageProps) => {
  return (
    <div role="status" className="p-3 rounded text-xs bg-bolt-ds-brandContainer text-bolt-ds-brand mx-3">
      {message}
    </div>
  );
});

Message.displayName = 'Message';

const ErrorMessage = memo(({ message }: MessageProps) => {
  return (
    <div className="p-3 rounded text-xs bg-bolt-elements-button-danger-background text-bolt-elements-button-danger-text mx-3">
      {message}
    </div>
  );
});

ErrorMessage.displayName = 'ErrorMessage';

const MaxContentLengthTracker = memo(() => {
  const chars = Array.from({ length: 200 }).map((_, i) => i % 2);
  const preRef = useRef<HTMLPreElement>(null);
  const width = useElementWidth(preRef);

  useEffect(() => {
    if (preRef.current) {
      const visibleChars = getMonospaceCharCount(preRef.current);
      workbenchStore.searchStore.renderedCharCount.set(visibleChars);
    }
  }, [width, preRef]);

  return (
    <div className="relative w-full">
      <pre ref={preRef} className="sr-only w-full truncate pl-7 pr-3 text-xs" aria-hidden="true">
        {chars.join('')}
      </pre>
    </div>
  );
});

MaxContentLengthTracker.displayName = 'MaxContentLengthTracker';

const SearchPanel = forwardRef<{ focusInput: () => void }>((_props, ref) => {
  const error = useStore(workbenchStore.searchStore.error);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focusInput: () => {
      searchInputRef.current?.focus();
    },
  }));

  return (
    <div className="flex-grow overflow-y-auto overflow-auto" role="search" aria-label="Search in files panel">
      <div className="gap-2.5 grid">
        <SearchInput ref={searchInputRef} />
        {error && <ErrorMessage message={error} />}
        {!error && <ResultList />}
      </div>
    </div>
  );
});

SearchPanel.displayName = 'SearchPanel';

export { SearchPanel };
